import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import toast from 'react-hot-toast'
import notificationApi from '@/api/admin/notificationApi'
import useMeasureOptions from '@/features/auth/hooks/useMeasureOptions'
import useUpdateUserProfile from '@/features/auth/hooks/useUpdateUser'
import useUser from '@/features/auth/hooks/useUser'
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  useTranslation,
} from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { changeTheme } from '@/store/slices/themeSlice'
import { mode_type } from '@/theme/types/constants'
import type { MEASURE } from '@/types'
import type { User } from '@/types/user'

/**
 * SCRUM-1032 — user settings for the guided setup.
 *
 * The wizard is a full-screen surface with its own header: it has none of the
 * app chrome, so `UserNav` is not on screen and the three settings that change
 * what the setup *looks like while you fill it in* are unreachable. Language,
 * units and theme are exactly the three worth having here — a user entering a
 * thousand decoders in the wrong unit finds out late.
 *
 * One popover with three rows rather than a menu of submenus: every option is
 * one click from the icon, and the current choice is visible without opening
 * anything further.
 */

const SEGMENT_HEIGHT = 30

interface GuidedSettingsMenuProps {
  /**
   * Writes what the open step has staged before the unit changes; resolves
   * false when anything was refused or a save is already running. Values are
   * converted when they are written, so a row typed in the old unit and
   * written after the switch would be stored as if typed in the new one.
   */
  beforeUnitChange?: () => Promise<boolean>
  /** Reloads the page once a language is chosen; injectable for tests. */
  reloadPage?: () => void
}

const GuidedSettingsMenu = ({
  beforeUnitChange,
  reloadPage = () => window.location.reload(),
}: GuidedSettingsMenuProps) => {
  const { t, currentLanguage, switchLanguage } = useTranslation()
  const dispatch = useDispatch()
  const themeMode = useSelector(selectTheme)
  const { data: user } = useUser<User>()
  const { data: measureOptions } = useMeasureOptions()
  const updateProfile = useUpdateUserProfile()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const close = () => setAnchorEl(null)

  const handleLanguage = async (language: SupportedLanguage) => {
    if (language === currentLanguage) return close()
    // Every component holds its own copy of the language, so the switch only
    // reaches the rest of the page through a reload — without one the menu
    // turned Korean and the wizard stayed English (D2 retest, 29 Oct). The
    // step is written first, so the reload loses nothing staged.
    if (beforeUnitChange && !(await beforeUnitChange())) {
      toast.error(t('guidedSetupLanguageChangeHeld'), {
        id: 'guided-language-held',
      })
      return close()
    }
    try {
      if (user?.email) {
        // Best effort: the alert emails follow the same language. A failure
        // here must not stop the UI switching.
        await notificationApi
          .updateSubscriptionInfo({ email: user.email, language })
          .catch(() => undefined)
      }
      await switchLanguage(language)
    } finally {
      close()
    }
    reloadPage()
  }

  const handleMeasure = async (measure: MEASURE) => {
    if (measure.id === user?.measure?.id) return close()
    if (beforeUnitChange && !(await beforeUnitChange())) {
      toast.error(t('guidedSetupUnitChangeHeld'), { id: 'guided-unit-held' })
      return close()
    }
    if (user?.email) {
      await notificationApi
        .updateSubscriptionInfo({ email: user.email, measure: measure.name })
        .catch(() => undefined)
    }
    // Read straight from storage by the unit helpers, so it is set before the
    // profile round trip rather than after it.
    localStorage.setItem('measure', measure.name)
    updateProfile({ measure: String(measure.id) })
    close()
  }

  const handleTheme = (mode: string) => {
    if (mode === themeMode) return close()
    localStorage.setItem('theme', mode)
    dispatch(changeTheme(mode))
    close()
  }

  const segmentSx = (selected: boolean) => ({
    flex: 1,
    height: SEGMENT_HEIGHT,
    px: '10px',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: selected ? 'primary.main' : 'border.main',
    backgroundColor: (theme: any) =>
      selected
        ? theme.palette.mode === 'dark'
          ? 'rgba(102,240,160,0.14)'
          : 'rgba(0,71,68,0.08)'
        : 'transparent',
    color: selected ? 'primary.main' : 'text.main',
    fontSize: '13px',
    fontWeight: selected ? 600 : 400,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 140ms ease, border-color 140ms ease',
    '&:hover': { borderColor: 'primary.main' },
  })

  const row = (label: string, children: React.ReactNode) => (
    <Stack spacing={0.75}>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'grey.600',
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1}>
        {children}
      </Stack>
    </Stack>
  )

  return (
    <>
      <Tooltip title={t('userNavSettings')} arrow>
        <IconButton
          aria-label={t('guidedSetupUserSettings')}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            width: 34,
            height: 34,
            border: '1px solid',
            borderColor: anchorEl ? 'primary.main' : 'border.main',
            color: anchorEl ? 'primary.main' : 'text.main',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
          }}
        >
          <PersonRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              p: '16px',
              width: 268,
              borderRadius: '12px',
              backgroundColor: 'background.default',
              border: '1px solid',
              borderColor: 'border.main',
              boxShadow: '0 12px 32px rgba(16,24,40,0.16)',
            },
          },
        }}
      >
        <Stack spacing={2}>
          {row(
            t('userNavLanguage'),
            Object.values(SUPPORTED_LANGUAGES).map((language) => (
              <Box
                key={language.code}
                component="button"
                type="button"
                onClick={() => handleLanguage(language.code as SupportedLanguage)}
                sx={{
                  ...segmentSx(language.code === currentLanguage),
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <Box component="span" sx={{ fontSize: '14px' }}>
                  {language.flag}
                </Box>
                {language.name}
              </Box>
            )),
          )}

          {row(
            t('userNavUnitOfMeasurement'),
            (measureOptions ?? []).map((measure: MEASURE) => (
              <Box
                key={measure.id}
                component="button"
                type="button"
                onClick={() => handleMeasure(measure)}
                sx={segmentSx(measure.id === user?.measure?.id)}
              >
                {measure.name}
              </Box>
            )),
          )}

          {row(
            t('userNavTheme'),
            [
              { mode: mode_type.light, label: t('userNavThemesLight') },
              { mode: mode_type.dark, label: t('userNavThemesDark') },
            ].map((option) => (
              <Box
                key={option.mode}
                component="button"
                type="button"
                onClick={() => handleTheme(option.mode)}
                sx={segmentSx(option.mode === themeMode)}
              >
                {option.label}
              </Box>
            )),
          )}
        </Stack>
      </Popover>
    </>
  )
}

export default GuidedSettingsMenu
